import { createFileRoute, useNavigate } from "@tanstack/react-router";
import SimpleTable from "@/components/ui/table";
import { useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { useEffect } from "react";
import FullPagination from "@/components/ui/shared/full-pagination";
import {
  EmployeeRoleOptions,
  EmployeeStatusOptions,
  EmployeeTableColumns,
} from "@/components/employee/employee-meta";
import { SearchEngine } from "@/components/ui/shared/search-engine";
import { Package } from "lucide-react";
import type { Employee } from "@/store/Employee";
import { EmployeeModals } from "@/components/employee/employee-modals";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/hooks/use-translate";
import { Plus } from "lucide-react";

export const Route = createFileRoute("/(dasboard)/employees/")({
  component: () => <EmployeesPage />,
});

function EmployeesPage() {
  const { t, translateOptions } = useTranslate();
  const store = useAppStore();
  const navigate = useNavigate();

  const searchForm = useSimpleForm({
    keyword: undefined,
    status: undefined,
    employment_status: undefined,
    role: undefined,
  });

  const search = ({ page = 1, per_page = 10 } = {}) => {
    store.employee.fetch({ page, per_page, ...searchForm.values });
  };

  const handleAutoComplete = (values: any) => {
    store.employee.paginate(
      store.employee.items.filter((employee: Employee) => {
        const keywordMatch = values.keyword
          ? `${employee.first_name} ${employee.last_name}`
            .toLowerCase()
            .includes(values.keyword) ||
          employee.email.toLowerCase().includes(values.keyword)
          : true;

        const statusMatch = values.status
          ? employee.status === values.status
          : true;

        const employeeStatus = values.employment_status
          ? employee.employment_status === values.employment_status
          : true;

        const roleStatus = values.role ? employee.role === values.role : true;

        return keywordMatch && statusMatch && employeeStatus && roleStatus;
      })
    );
  };

  useEffect(() => {
    if (store.auth.session) search({});
  }, [store.auth.session]);
  return (
    <>
      <div className="max-w-7xl mx-auto">
        <EmployeeModals />

        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
          <h2 className="text-2xl font-semibold">{t("Employees")}</h2>
          <Button
            onClick={() => {
              navigate({ to: "/employees/create" });
            }}
          >
            <Plus className="w-4 h-4" />
            {t("Add employee")}
          </Button>
        </header>

        <div className="mb-4">
          <SearchEngine
            autoComplete={handleAutoComplete}
            selects={[
              {
                placeholder: t("All statuses"),
                options: translateOptions(EmployeeStatusOptions),
                name: "status",
                icon: <Package className="w-4 h-4" />,
              },
              {
                placeholder: t("All employments"),
                options: [
                  { value: "full-time", label: t("Full-time") },
                  { value: "part-time", label: t("Part-time") },
                  { value: "contract", label: t("Contract") },
                ],
                name: "employment_status",
                icon: <Package className="w-4 h-4" />,
                size: "w-full",
              },
              {
                placeholder: t("All roles"),
                options: translateOptions(EmployeeRoleOptions),
                name: "role",
                icon: <Package className="w-4 h-4" />,
              },
            ]}
            form={searchForm}
            onSubmit={undefined}
          />
        </div>
        <SimpleTable
          items={store.employee.pagination.data}
          columns={EmployeeTableColumns({
            onView: (employee: any) => {
              store.employee.setCurrent(employee);
              navigate({ to: `/employees/${employee.id}/view` });
            },
            onEdit: (employee: any) => {
              store.employee.setCurrent(employee);
              navigate({ to: `/employees/${employee.id}/view` });
            },
            onDelete: async (employee: any) => {
              store.employee.setCurrent(employee);
              store.display.show("delete_employee");
            },
          })}
        />
        <FullPagination
          className="mt-4"
          canEdit={true}
          pagination={store.employee.pagination}
          onGoto={(page: number) => search({ page })}
          onPerPage={(per_page) => search({ per_page })}
        />
      </div>
    </>
  );
}
